import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Skeleton,
  Typography,
} from '@mui/material';
import { Lock, LockOpen } from '@mui/icons-material';
import EnrollmentDialog from './EnrollmentDialog';
import DisableMfaDialog from './DisableMfaDialog';
import { getMfaStatus, formatEnrolledAt, SessionExpiredError } from '../../services/mfaService';

const OFF_DESCRIPTION = 'Add a second step at sign-in using an authenticator app.';

/**
 * Branch the sentence, not the value.
 *
 * Top-level enrolled_at is a *int64 with no omitempty, so it arrives as null
 * rather than absent, and interpolating formatEnrolledAt's fallback would read
 * "On since Unknown." — which looks like a bug rather than a missing date. The
 * state being reported is that MFA is on; the date is a detail we may not have.
 */
const enrolledSince = (enrolledAt) => {
  const formatted = formatEnrolledAt(enrolledAt);
  return formatted === 'Unknown' ? 'On for this account.' : `On since ${formatted}.`;
};

/**
 * Multi-factor authentication settings.
 *
 * This tab exists only on the account page. It is never rendered by UserDetail,
 * and the guard is structural rather than a conditional: the tab list is a prop
 * of the identity rail, and UserDetail's list does not contain "Security". On
 * top of that, every endpoint behind it acts on the bearer token's own user and
 * takes no user id, so there is nothing here that could be pointed at somebody
 * else even by mistake.
 *
 * MFA is presented as one on/off setting rather than a list of removable
 * factors. The server clears every factor on unenroll and its enrollment guards
 * ensure at most one can exist (mfa_service.go:60-75), so factors are shown as
 * information and the action is a single toggle.
 */
function SecurityTab() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [enrolling, setEnrolling] = useState(false);
  const [disabling, setDisabling] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setStatus(await getMfaStatus());
    } catch (err) {
      if (!(err instanceof SessionExpiredError)) {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const afterChange = useCallback(
    (message) => {
      setEnrolling(false);
      setDisabling(false);
      setNotice(message);
      // Re-fetch rather than patching local state, so what is on screen is what
      // the server says — the same rule UserDetail follows after a role change.
      load();
    },
    [load],
  );

  const enrolled = Boolean(status?.enrolled);
  const factors = status?.factors || [];
  // At most one factor can exist, so the first is the one to remove. Guarded
  // because an enrolled status with no factors is not worth crashing over.
  const factorId = factors[0]?.id;

  if (loading && !status) {
    return <Skeleton variant="rectangular" height={160} />;
  }

  return (
    <Box>
      <Typography
        variant="h6"
        gutterBottom
        sx={{ fontSize: '1.125rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}
      >
        Security
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={load}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, py: 2 }}>
        <Box sx={{ color: enrolled ? 'success.main' : 'text.secondary', mt: 0.5 }}>
          {enrolled ? <Lock /> : <LockOpen />}
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Multi-factor authentication
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {enrolled ? enrolledSince(status.enrolled_at) : OFF_DESCRIPTION}
          </Typography>
        </Box>
        {status &&
          (enrolled ? (
            <Button
              variant="outlined"
              color="error"
              onClick={() => setDisabling(true)}
              disabled={!factorId}
            >
              Turn off
            </Button>
          ) : (
            <Button variant="contained" onClick={() => setEnrolling(true)}>
              Turn on
            </Button>
          ))}
      </Box>

      {enrolled && factors.length === 0 && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          Multi-factor authentication is on, but no authenticator is listed. Contact support
          to reset it.
        </Alert>
      )}

      {factors.map((factor) => (
        <Box
          key={factor.id}
          data-testid={`mfa-factor-${factor.id}`}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            py: 1.5,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 180 }}>
            {factor.display_name || 'Authenticator app'}
          </Typography>
          <Chip label={factor.type} size="small" sx={{ fontWeight: 600 }} />
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="caption" color="text.secondary">
            {`Added ${formatEnrolledAt(factor.enrolled_at)}`}
          </Typography>
        </Box>
      ))}

      <EnrollmentDialog
        open={enrolling}
        onClose={() => setEnrolling(false)}
        onEnrolled={afterChange}
      />
      <DisableMfaDialog
        open={disabling}
        onClose={() => setDisabling(false)}
        factorId={factorId}
        onDisabled={() => afterChange('Multi-factor authentication is off.')}
      />
    </Box>
  );
}

export default SecurityTab;
